import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { useAuth } from "@/hooks/useAuth";
import { ApiError, ApiUnavailableError } from "@/api/client";

const SSO_ERROR_MESSAGES: Record<string, string> = {
  flow_expired: "The sign-in attempt expired. Please try again.",
  state_mismatch: "The sign-in attempt could not be verified. Please try again.",
  access_denied: "Sign-in was cancelled at the identity provider.",
  provider_error: "The identity provider reported an error. Please try again.",
  provider_unavailable: "The identity provider is not reachable right now.",
  missing_code: "The identity provider did not return a sign-in code.",
  exchange_failed: "KnotFin could not complete sign-in with the identity provider.",
  invalid_token: "The identity provider returned a token KnotFin could not verify.",
  unknown_account: "No KnotFin account is linked to that identity. Ask an administrator.",
  account_disabled: "That account is disabled.",
  account_conflict: "That username already belongs to a different identity. Ask an administrator.",
};

// Reads ?sso_error= left by /api/v1/auth/oidc/callback and clears it from the
// address bar so a later password sign-in does not land the router on /login.
function consumeSsoError(): string | null {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("sso_error");
  if (!code) {
    return null;
  }
  const message = SSO_ERROR_MESSAGES[code] ?? "Single sign-on failed. Please try again.";
  if (window.location.pathname === "/login") {
    window.history.replaceState(null, "", "/");
  } else {
    params.delete("sso_error");
    const search = params.toString();
    window.history.replaceState(null, "", window.location.pathname + (search ? `?${search}` : "") + window.location.hash);
  }
  return message;
}

// The deep link the visitor was on, handed to the provider round-trip so they
// land back there. The callback only honours same-origin paths.
function currentReturnTo(): string {
  const { pathname, search, hash } = window.location;
  if (pathname === "/login" || pathname === "/") {
    return "/";
  }
  return pathname + search + hash;
}

export function LoginPage() {
  const { login, sso } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(!sso.enabled || !sso.hide_password_login);

  useEffect(() => {
    const ssoError = consumeSsoError();
    if (ssoError) {
      setError(ssoError);
    }
  }, []);

  useEffect(() => {
    setShowPasswordForm(!sso.enabled || !sso.hide_password_login);
  }, [sso.enabled, sso.hide_password_login]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login({ username, password });
    } catch (err) {
      if (err instanceof ApiUnavailableError) {
        setError(err.message);
      } else
      if (err instanceof ApiError) {
        setError(err.problem.detail);
      } else {
        setError("Failed to sign in. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  function startSso() {
    const startPath = sso.start_path ?? "/api/v1/auth/oidc/start";
    window.location.assign(`${startPath}?return_to=${encodeURIComponent(currentReturnTo())}`);
  }

  const providerName = sso.provider_name || "SSO";

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-brand">
          <span className="login-brand-mark-frame" aria-hidden="true">
            <img className="login-brand-mark" src="/knotfin-logo-v3.png" alt="" />
          </span>
          <h1 className="login-brand-title">KnotFin</h1>
        </div>
        {error && (
          <p className={`settings-status ${error.includes("API not responding") ? "settings-status--warning" : "settings-status--error"}`}>
            {error}
          </p>
        )}
        {sso.enabled && (
          <div className="login-sso">
            <button type="button" className="login-sso-btn" onClick={startSso}>
              Sign in with {providerName}
            </button>
            {showPasswordForm ? (
              <div className="login-divider" aria-hidden="true"><span>or</span></div>
            ) : (
              <button type="button" className="login-link-btn" onClick={() => setShowPasswordForm(true)}>
                Sign in with a password instead
              </button>
            )}
          </div>
        )}
        {showPasswordForm && (
          <form onSubmit={handleSubmit}>
            <label>
              Username
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                required
              />
            </label>
            <label>
              Password
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </label>
            <button type="submit" disabled={submitting}>
              {submitting ? "Signing in..." : "Sign in"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
